<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="帯域上限値設定"
  >
    <template v-slot:body="{ cancel }" v-if="isLoaded">
      <cross-validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <v-row>
                  <v-col>
                    <app-label-group label="回線番号" :value="enumber" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <app-label-group label="帯域">
                      <template #value>
                        <span class="ml-3"
                          >{{
                            $filter.bandwidthTo(bandwidth, "Mbps")
                          }}(設定可能帯域：<span
                            :class="{
                              'text-red': remainingBandwidth.value < 0,
                            }"
                            >{{ $filter.bandwidthTo(remainingBandwidth) }}</span
                          >)</span
                        >
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>

                <app-table
                  primary-key="menu"
                  :fields="fields"
                  :items="items"
                  sticky-header="noSticky"
                  :tr-class="() => undefined"
                >
                  <!--メニュー-->
                  <template #cell(cloudServiceMenu)="{ item }">
                    {{ $filter.cloudServiceMenuTo(item.cloudServiceMenu) }}
                  </template>

                  <!--帯域上限値-->
                  <template #head(bandwidth)>
                    <app-badge item="帯域上限値" :isInfo="false"></app-badge>
                  </template>
                  <!--帯域値-->
                  <template #cell(bandwidth)="{ item }">
                    <validation-provider
                      :name="item.cloudLineSeq + 'bandwidth'"
                      :label="'帯域上限値'"
                      v-model="item.bandwidth"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select-search
                        class="mt-5"
                        :items="bandwidthSelectList(item)"
                        item-title="text"
                        item-value="value"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </template>

                  <!--備考-->
                  <template #head(description)>
                    <app-badge
                      item="備考"
                      :isInfo="true"
                      :is-required="false"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '200' })
                      "
                    ></app-badge>
                  </template>

                  <!--備考-->
                  <template #cell(description)="{ item }">
                    <validation-provider
                      :name="item.cloudLineSeq + 'description'"
                      :label="'備考'"
                      v-model="item.description"
                      :rules="{ max: { length: 200 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-textarea
                        class="mt-1"
                        rows="2"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                        :hide-details="!errorMessage"
                      ></app-form-textarea>
                    </validation-provider>
                  </template>
                </app-table>
              </v-container>
            </div>

            <cross-validation-provider
              vid="no_change"
              :passed="cloudLineChanged"
              class="my-4"
            >
              {{ $filter.message("no_change_data") }}
            </cross-validation-provider>
            <cross-validation-provider
              vid="no_multiple_change"
              :passed="cloudLineNotChangedMoreThanOne"
              class="my-4"
            >
              {{ $filter.message("more_than_one_change") }}
            </cross-validation-provider>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">設定</v-btn>
                <v-btn color="primary" variant="outlined" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import type { CloudLine, CloudMsPut, Line } from "@/apis/CloudLineApi";
import { maxBandwidthOptions } from "@/views/nwSetting/multicloud/MultiCloudList.vue";
import cloneDeep from "lodash/cloneDeep";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "MultiCloudBandwidthLimitModify",
  props: {
    /** 回線番号 */
    enumber: {
      type: String as PropType<string>,
      required: true,
    },
    /** 回線帯域 */
    bandwidth: {
      type: Object as PropType<Line["bandwidth"]>,
      required: true,
    },
    /** 取得したクラウド回線情報全件 */
    cloudLineList: {
      type: Array as PropType<CloudLine[]>,
      required: true,
    },
    /** クラウド回線排他情報 */
    cloudLineKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      fields: [
        { key: "cloudServiceMenu", title: "メニュー", width: "140px" },
        {
          key: "bandwidth",
          title: "帯域上限値",
          width: "170px",
        },
        {
          key: "description",
          title: "備考",
          width: "200px",
        },
      ],
      cloudLineItems: [] as CloudLine[],
      items: [] as CloudLine[],
      /** クラウド帯域値のデータマップ */
      cloudUpperLimitMap: {} as Record<string, UpperLimitEntity | undefined>,
      isLoaded: false,
    };
  },
  async mounted() {
    this.items = this.cloudLineList.map((e) => ({
      ...e,
      bandwidth:
        e.bandwidth.value === 0 ? { value: 0, unit: null } : e.bandwidth,
    }));
    this.cloudLineItems = cloneDeep(this.items);

    // クラウド帯域値を取得する
    const promises = this.cloudLineItems.map(async (e) => {
      const limit = await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: "MULTI_CLOUD",
        enumber: this.enumber,
        cloudLineSeq: e.cloudLineSeq,
      });
      this.cloudUpperLimitMap[e.cloudLineSeq] =
        limit.cloudLine!.upperLimitList!.find(
          (v: { upperLimitItemName: string }) =>
            v.upperLimitItemName === "クラウド帯域値"
        );
    });
    await Promise.all(promises);
    this.isLoaded = true;
  },

  computed: {
    cloudLineChanged(): boolean {
      return this.$crossValidation.cloudLineChanged(
        this.cloudLineItems,
        this.items
      );
    },
    cloudLineNotChangedMoreThanOne(): boolean {
      return this.$crossValidation.cloudLineNotChangedMoreThanOne(
        this.cloudLineItems,
        this.items
      );
    },
    modifyList(): CloudLine[] {
      return this.items.filter((v, index) => {
        const newBandwidth: string =
          v.bandwidth.value.toString() + v.bandwidth.unit;
        const oldBandwidth: string =
          this.cloudLineItems[index].bandwidth.value.toString() +
          this.cloudLineItems[index].bandwidth.unit;
        return (
          v.description !== this.cloudLineItems[index].description ||
          newBandwidth !== oldBandwidth
        );
      });
    },
    /** 残りの設定可能帯域 */
    remainingBandwidth(): { value: number; unit: "MBPS" | "GBPS" } {
      const limitBandwidthMbps =
        (this.$filter.bandwidthToBps(this.bandwidth) ?? 0) / 1000 / 1000;
      const sumBandwidthMbps = this.items.reduce(
        (acc, e) =>
          acc + (this.$filter.bandwidthToBps(e.bandwidth) ?? 0) / 1000 / 1000,
        0
      );
      const diffBandwidthMbps = limitBandwidthMbps - sumBandwidthMbps;
      if (diffBandwidthMbps >= 1000 || diffBandwidthMbps <= -1000) {
        return { value: diffBandwidthMbps / 1000, unit: "GBPS" };
      } else {
        return { value: diffBandwidthMbps, unit: "MBPS" };
      }
    },
  },
  /**  帯域選択リストを取得する */
  methods: {
    bandwidthSelectList(item: CloudLine) {
      // 帯域値がnullの場合、元の帯域値を設定する
      if (item.bandwidth === null) {
        const original = this.cloudLineItems.find(
          (e) => e.cloudLineSeq === item.cloudLineSeq
        );
        if (original) {
          item.bandwidth = {
            value: original.bandwidth.value,
            unit: original.bandwidth.unit,
          };
        }
      }

      return maxBandwidthOptions.filter((v) => {
        const bps = this.$filter.bandwidthToBps(v.value)!;
        const bandwidthBps = this.$filter.bandwidthToBps(this.bandwidth)!;
        const diff = this.$filter.bandwidthToBps(this.remainingBandwidth) ?? 0;
        // クラウド帯域値が存在する場合、上限値を取得する、存在しない場合、現在は100Gbpsまで設定できる
        const cloudLineMaxBandwidthBps = this.$filter.bandwidthToBps({
          value: this.cloudUpperLimitMap[item.cloudLineSeq]?.upperLimit
            ? this.cloudUpperLimitMap[item.cloudLineSeq]!.upperLimit
            : 100,
          unit: "GBPS",
        });
        if (diff > 0) {
          if (bps > diff + this.$filter.bandwidthToBps(item.bandwidth)!) {
            return false;
          }
        } else {
          if (bps > this.$filter.bandwidthToBps(item.bandwidth)!) {
            return false;
          }
        }
        // MSの場合、<=10Gとする
        if (item.cloudServiceMenu.startsWith("MS")) {
          return (
            bps <= bandwidthBps &&
            bps <= cloudLineMaxBandwidthBps! &&
            bps <= 10 * 1000 * 1000 * 1000
          );
        } else if (item.cloudServiceMenu !== "KDDI_GPU_CLOUD") {
          // MS,GPU以外の場合、<=50Gとする
          return (
            bps <= bandwidthBps &&
            bps <= cloudLineMaxBandwidthBps! &&
            bps <= 50 * 1000 * 1000 * 1000
          );
        } else {
          // KDDI_GPU_CLOUDの場合、0Mbpsまたは1Gbps単位（<=100G）のみとし、10Mbps~900Mbpsは選択不可とする
          return (
            v.value.unit !== "MBPS" &&
            bps <= bandwidthBps &&
            bps <= cloudLineMaxBandwidthBps!
          );
        }
      });
    },
    async submit() {
      await this.$confirm(`帯域上限値を設定します。よろしいですか？`);
      const putData: CloudMsPut = {
        cloudLineKeyId: this.cloudLineKeyId,
        bandwidth: this.modifyList[0].bandwidth,
        description: this.modifyList[0].description,
        note: null,
      };
      await this.$api.cloudLine.putMsCloudLine(
        this.modifyList[0].cloudLineSeq,
        putData
      );
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
